import type { BreadcrumbResponse, ContentSearchResponse, FacetResponse, FacetValueResponse, ProductSearchResponse, QueryValue } from '../api/search';
import productGuideFacetsJson from '../productGuide/productGuideFacetsJson.json';
import contentFacetsJson from '../search/contentFacets.json';
import productFacetsJson from '../search/productFacets.json';
import removeDiacritics from '../utils/removeDiacritics';
import type { ContentSearchResult, ProductSearchResult } from './Search';
import type { Store, WineStyle } from './types';

export type UserPrefs = null | undefined | { favouriteStore?: Store; redWineStyle?: WineStyle; whiteWineStyle?: WineStyle };

export function configureProductFacets(data: ProductSearchResponse, user: UserPrefs): ProductSearchResult {
    return {
        ...data,
        facets: configureFacets(data.facets, data.breadcrumbs, productFacetsJson, user),
    };
}

export function configureProductGuideFacets(data: ProductSearchResponse, user: UserPrefs) {
    const facets = data.facets
        .map((facet) => configureFacet(facet, getFacet(facet, productGuideFacetsJson), user))
        .filter(Boolean)
        .sort((a, b) => b.priority - a.priority);

    return {
        anyFacets: facets.length > 0,
        facets: getSelectableFacets(facets, false),
        breadcrumbs: configureBreadcrumbs(data.breadcrumbs, productGuideFacetsJson),
    };
}

export function configureContentFacets(data: ContentSearchResponse): ContentSearchResult {
    return {
        ...data,
        facets: configureFacets(data.facets ?? [], data.breadcrumbs ?? [], contentFacetsJson),
    };
}

function getFacet(facet: FacetResponse, json: FacetSearchJson): FacetJson | undefined {
    const found = json.facets[facet.code];
    if (found) return found;

    if (!json.default) return undefined;

    const singleLevel = facet.values.every((v) => v.code === 'true');
    if (singleLevel) {
        return {
            ...json.default,
            group: 'singleValues',
            booleanFacet: true,
        };
    }

    return json.default;
}

function configureBreadcrumb(breadcrumb: BreadcrumbResponse, json: FacetJson | undefined): Breadcrumb {
    return {
        ...breadcrumb,
        removeQuery: getQuery(breadcrumb.removeQuery),
        priority: json?.priority ?? 0,
    };
}

function configureBreadcrumbs(breadcrumbs: BreadcrumbResponse[], json: FacetSearchJson) {
    return breadcrumbs.map((b) => configureBreadcrumb(b, getBreadcrumb(b, json))).sort((a, b) => b.priority - a.priority);
}

function getBreadcrumb(breadcrumb: BreadcrumbResponse, json: FacetSearchJson): FacetJson | undefined {
    return json.facets[breadcrumb.facetCode] ?? json.default;
}

function configureFacets(
    facets: FacetResponse[],
    breadcrumbs: BreadcrumbResponse[],
    json: FacetSearchJson,
    user?: UserPrefs,
    allowMultiSelectIfPossible = true,
): FacetData {
    const configuredFacets = facets.map((facet) => configureFacet(facet, getFacet(facet, json), user)).filter(Boolean);

    const selectableFacets = getSelectableFacets(configuredFacets, allowMultiSelectIfPossible);

    const groupedFacets = configureGroups(selectableFacets, json);

    return {
        any: facets.length + breadcrumbs.length > 0,
        groupedFacets,
        breadcrumbs: configureBreadcrumbs(breadcrumbs, json),
    };
}

// grouping facets together based on configuration
// each facet must be configured with group property, with a corresponding group configuration
function configureGroups(facets: Facet[], json: FacetSearchJson) {
    const groupedFacets = new Map<string, FacetGroup>();
    for (const facet of facets) {
        const code = facet.group ?? facet.code;

        const facetGroup = groupedFacets.get(code);
        if (facetGroup) {
            facetGroup.facets.push(facet);
        } else {
            const { name, priority, expanded } = facet.group && json.groups ? json.groups[facet.group] : facet;
            groupedFacets.set(code, {
                code,
                name,
                priority,
                expanded,
                facets: [facet],
            });
        }
    }

    return [...groupedFacets.values()].sort((a, b) => b.priority - a.priority);
}

function getSelectableFacets(facets: Facet[], allowMultiSelectIfPossible: boolean) {
    const selectedFacets = facets.filter((f) => f.values.some((v) => v.selected));
    const unselectedFacets = facets.filter((f) => f.values.every((v) => !v.selected));

    if (allowMultiSelectIfPossible) {
        const multiSelectableFacets = selectedFacets
            .filter((f) => f.multiSelect)
            .map((facet) => ({
                ...facet,
                values: facet.values.filter((v) => !v.selected),
            }))
            .filter((f) => f.values.length);
        unselectedFacets.push(...multiSelectableFacets);
    }

    return unselectedFacets;
}

// adding/overriding properties for facets and facet values
function configureFacet(facet: FacetResponse, facetJson: FacetJson | undefined, user: UserPrefs | undefined): Facet | undefined {
    if (!facetJson) return undefined;

    return {
        ...facet,
        ...facetJson,
        values: facet.values.map(
            (value) =>
                ({
                    ...value,
                    ...getQueryAndHref(value.query),
                    iconImageClass: getFacetIcon(facetJson, value, facet, user),
                    get normalizedName() {
                        this.__normalizedName ??= removeDiacritics(value.name);
                        return this.__normalizedName;
                    },
                }) satisfies FacetValue,
        ),
    } satisfies Facet;
}

function getFacetIcon(
    facetJson: FacetJson | undefined,
    value: FacetValueResponse,
    facet: FacetResponse,
    user: UserPrefs | undefined,
): string | undefined {
    if (facet.code === 'availableInStores' && value.code === user?.favouriteStore?.name) return 'icon-my-store';
    if (facet.code === 'styleCategory' && (value.code === user?.redWineStyle?.code || value.code === user?.whiteWineStyle?.code))
        return 'icon-my-style';
    if (!facetJson?.icons) return undefined;
    return `${facet.code} ${facetJson.values?.[value.code]?.iconImageClass ?? value.code.replaceAll(' ', '-')}`;
}

function getQueryAndHref(queryValue: QueryValue): Pick<FacetValue, 'href' | 'query'> {
    const query = getQuery(queryValue);
    return {
        query,
        href: '?' + new URLSearchParams({ q: query }).toString(),
    };
}

export function getQuery({ query: { value } }: QueryValue): string {
    return value;
}

export interface FacetData {
    any: boolean;
    groupedFacets: FacetGroup[];
    breadcrumbs: Breadcrumb[];
}

export interface Breadcrumb {
    facetCode: string;
    facetName: string;
    facetValueCode: string;
    facetValueName: string;
    removeQuery: string;
    priority: number;
}

export interface Facet {
    code: string;
    multiSelect: boolean;
    name: string;
    priority: number;
    expanded?: boolean;
    icons?: boolean;
    listClass?: string;
    booleanFacet?: boolean;
    group?: string;
    values: FacetValue[];
    userInputtedInterval?: boolean;
    valueFrom?: number;
    valueTo?: number;
    alternateSort?: string;
}

export interface FacetGroup {
    code: string;
    expanded?: boolean;
    facets: Facet[];
    name: string;
    priority: number;
}

export interface FacetValue {
    code: string;
    count: number;
    name: string;
    description?: string;
    selected: boolean;
    query: string;
    href: string;
    iconImageClass?: string;
    __normalizedName?: string;
    normalizedName: string;
}

interface FacetSearchJson {
    default?: FacetJson;
    facets: Record<string, FacetJson>;
    groups?: Record<string, FacetGroupJson>;
}

interface FacetJson {
    icons?: boolean;
    expanded?: boolean;
    name?: string;
    listClass?: string;
    booleanFacet?: boolean;
    group?: string;
    values?: Record<string, FacetValueJson>;
    priority?: number;
    alternateSort?: string;
}

interface FacetValueJson {
    iconImageClass?: string;
}

interface FacetGroupJson {
    name: string;
    priority: number;
    expanded?: boolean;
}
